import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';

import { useGetDataPipelineSignedUrlQuery } from '../../requests/dataPipeline.gql.generated';
import { DownloadIconButton } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { errorToastConfig } from '~/packages/common-frontend/constants';

type DownloadItemProps = {
  displayText: string;
  filename: string;
  url: string;
};
const DownloadItem = ({ displayText, filename, url }: DownloadItemProps) => {
  const handleDownload = () => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  return (
    <MenuItem>
      <HStack onClick={() => handleDownload()}>
        <Text>{displayText}</Text>

        <DownloadIconButton
          isDisabled={!url}
          ariaLabel="download"
          label="Download"
        />
      </HStack>
    </MenuItem>
  );
};

export const DownloadMenuList = (props: { artifactInstanceId: string }) => {
  const toast = useToast();
  const { artifactInstanceId } = props;
  const { data, error } = useGetDataPipelineSignedUrlQuery({
    variables: {
      serializedParams: JSON.stringify({
        artifactInstanceId,
      }),
    },

    // TTL on this signed URL is 4 hours.
    // - Always fetch a fresh URL when the download menu is reopened
    // - Refresh the URL every hour the download menu remains open
    fetchPolicy: 'network-only',
    pollInterval: 60 * 60 * 1000,
  });

  useEffect(() => {
    if (error) {
      errorToastConfig.title = error.message;
      toast(errorToastConfig);
    }
  }, [error, toast]);

  if (!data) {
    return (
      <MenuList>
        <MenuItem>Loading...</MenuItem>
      </MenuList>
    );
  }

  if (data.downloadUrls.length === 0) {
    return (
      <MenuList>
        <MenuItem>No downloads available</MenuItem>
      </MenuList>
    );
  }

  return (
    <MenuList>
      {data.downloadUrls.map(({ url, filename }) => (
        <DownloadItem
          key={url}
          url={url}
          filename={filename}
          displayText={filename}
        />
      ))}
    </MenuList>
  );
};

export const DownloadMenuButton = ({
  artifactInstanceId,
  buttonLabel,
}: {
  buttonLabel: string;
  artifactInstanceId: string;
}) => {
  // Utilize Chakra's useDisclosure hook to manage the open state of the menu
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Box>
      <Menu isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <MenuButton
          as={Button}
          leftIcon={<Icon icon="download" />}
          variant="ghost"
          border="2px solid"
          onClick={() => !isOpen && onOpen()} // Ensures onOpen is called when the menu is not already open
        >
          {buttonLabel || 'Download'}
        </MenuButton>
        {isOpen && <DownloadMenuList artifactInstanceId={artifactInstanceId} />}
      </Menu>
    </Box>
  );
};
